import { useEffect, useState } from "react";

import {
  BreadcrumbProps,
  Breadcrumbs,
  Classes,
  Colors,
} from "@blueprintjs/core";
import styled from "@emotion/styled";
import { Emoji, EmojiStyle } from "emoji-picker-react";
import { Link, useNavigate, useParams } from "react-router";

import { isWorkspaceAdmin } from "@/components/helpers/workspace/permissions";
import { Box, Flex } from "@/components/layout/flexbox";
import { Media } from "@/components/layout/Media";
import { useAuth } from "@/components/lib/auth";
import { AddToCollectionDialog } from "@/components/pages_logged_in/roleplays/AddToCollectionDialog";
import { AssignScenarioDialog } from "@/components/pages_logged_in/roleplays/AssignScenarioDialog";
import { MySessionsButton } from "@/components/pages_logged_in/roleplays/MySessionsButton";
import {
  buildRoleplayUrl,
  buildScenarioUrl,
} from "@/components/pages_logged_in/roleplays/utils";
import { Container } from "@/components/pieces/bootstrap";
import { CloneScenarioDialog } from "@/components/pieces/roleplays/CloneScenarioDialog";
import { RoleplayLimitBanner } from "@/components/pieces/roleplays/RoleplayLimitBanner";
import { ScenarioDeleteConfirm } from "@/components/pieces/roleplays/ScenarioDeleteConfirm";
import { ScenarioMenu } from "@/components/pieces/roleplays/ScenarioMenu";
import { ScenarioShare } from "@/components/pieces/roleplays/ScenarioShare";
import { Tabs } from "@/components/pieces/Tabs";
import { Button } from "@/components/ui/button";
import { SUBTEXT_COLOR } from "@/css/constants";
import { FullScenario, RoleplayPage, ScenarioModal } from "@/types/roleplays";
import { Tab } from "@/types/tabs";

const ROLEPLAY_DASHBOARD_ROUTE = {
  href: "/roleplays",
  text: "Roleplay Dashboard",
};

interface UseBreadcrumbsProps {
  page: RoleplayPage;
  roleplay?: ScenarioHeaderProps["roleplay"];
  scenario?: FullScenario | null;
}

function useBreadcrumbs({
  page,
  roleplay,
  scenario,
}: UseBreadcrumbsProps): BreadcrumbProps[] | undefined {
  const { roleplaySlug } = useParams();

  const roleplayText = roleplay?.name || "Loading";

  const roleplayUrl = roleplay?.slug ? buildRoleplayUrl(roleplay.slug) : "#";

  if (page === "scenarioDetail") {
    const breadcrumbs: { href?: string; text: string }[] = [
      ROLEPLAY_DASHBOARD_ROUTE,
    ];

    if (roleplaySlug) {
      breadcrumbs.push({
        href: roleplayUrl,
        text: roleplayText,
      });
    }
    breadcrumbs.push({ text: scenario?.name || "Loading" });
    return breadcrumbs;
  }
  if (page === "scenarioCreate") {
    const items = [ROLEPLAY_DASHBOARD_ROUTE, { text: "Create Scenario" }];
    if (roleplay) {
      items.splice(1, 0, {
        href: roleplayUrl,
        text: roleplayText,
      });
    }
    return items;
  }
  if (page === "intakeProcessing") {
    return [
      ROLEPLAY_DASHBOARD_ROUTE,
      {
        href: roleplayUrl,
        text: roleplayText,
      },
      { text: "Generating" },
    ];
  }
}

interface HeaderTitleProps {
  page: RoleplayPage;
  roleplay?: ScenarioHeaderProps["roleplay"];
  scenario?: FullScenario | null;
}

function HeaderTitle({ page, roleplay, scenario }: HeaderTitleProps) {
  if (page === "scenarioDetail") {
    const text = scenario?.name || "Loading...";
    return roleplay ? (
      <Link
        className="color-inherit"
        to={buildScenarioUrl({
          roleplaySlug: roleplay.slug,
          scenarioSlug: scenario?.slug ?? "",
        })}
      >
        <h1 className="m-0 text-[28px]">{text}</h1>
      </Link>
    ) : (
      <h1 className="m-0 text-[28px]">{text}</h1>
    );
  }
  if (page === "intakeProcessing") {
    return <h1 className="m-0 text-[28px]">Generating Scenario</h1>;
  }

  if (page === "scenarioCreate" && roleplay) {
    return (
      <Link
        className="color-inherit"
        to={buildRoleplayUrl(roleplay?.slug ?? "")}
      >
        <h1 className="m-0 text-[28px]">{roleplay?.name || "Loading..."}</h1>
      </Link>
    );
  }
  return null;
}

export function ScenarioDetailMenu({
  scenario,
  onEditScenario,
  refetchAssignments,
}: {
  scenario: FullScenario;
  onEditScenario: () => void;
  refetchAssignments: () => void;
}) {
  const [currentActiveModal, setCurrentActiveModal] = useState<ScenarioModal>();
  const { currentWorkspace } = useAuth();
  const isAdmin = isWorkspaceAdmin();
  const [assignScenarioOpen, setAssignScenarioOpen] = useState(false);
  return (
    <div className="flex gap-2">
      {isAdmin && (
        <>
          <Button
            intent="secondary"
            className="h-10"
            onClick={() => {
              setAssignScenarioOpen(true);
            }}
          >
            Assign
          </Button>
          <AssignScenarioDialog
            open={assignScenarioOpen}
            onOpenChange={() => setAssignScenarioOpen(false)}
            scenarioId={scenario.id}
            scenarioSlug={scenario.slug}
            onSuccess={() => {
              setAssignScenarioOpen(false);
              refetchAssignments();
            }}
          />
        </>
      )}

      {!currentWorkspace.isPersonal && <ScenarioShare scenario={scenario} />}

      <MySessionsButton
        scenarioId={scenario.id}
        roleplaySlug={scenario.collections?.[0]?.slug}
      />

      <ScenarioMenu
        scenario={scenario}
        onDelete={() => {
          setCurrentActiveModal("deleteScenario");
        }}
        onCloneScenario={() => {
          setCurrentActiveModal("cloneScenario");
        }}
        onAddToCollection={() => {
          setCurrentActiveModal("addToCollection");
        }}
        onEditScenario={onEditScenario}
      />

      <ScenarioDeleteConfirm
        open={currentActiveModal === "deleteScenario"}
        scenario={scenario}
        onClose={() => setCurrentActiveModal(undefined)}
      />
      <CloneScenarioDialog
        open={currentActiveModal === "cloneScenario"}
        scenario={scenario}
        onClose={() => setCurrentActiveModal(undefined)}
      />

      <AddToCollectionDialog
        open={currentActiveModal === "addToCollection"}
        scenario={scenario}
        onClose={() => setCurrentActiveModal(undefined)}
      />
    </div>
  );
}

interface ScenarioHeaderProps {
  page: RoleplayPage;
  roleplay?: {
    id: string;
    name: string;
    slug: string;
    emojiUnicode: string;
  } | null;
  scenario?: FullScenario | null;
  loading?: boolean;
  px?: number;
  py?: number;
  noMargins?: boolean;
  onEditScenario?: () => void;
  refetchAssignments?: () => void;
}

export function ScenarioHeader({
  page,
  px = 0,
  py = 4,
  noMargins = false,
  roleplay,
  scenario,
  loading = false,
  onEditScenario = () => {},
  refetchAssignments = () => {},
}: ScenarioHeaderProps) {
  const isAdmin = isWorkspaceAdmin();

  const SCENARIO_TABS_CONFIG = [
    {
      key: `/scenarios/${scenario?.slug}`,
      label: "Practice",
      value: "practice",
    },
    ...(isAdmin
      ? [
          {
            key: `/scenarios/${scenario?.slug}/assignments`,
            label: "Assignments",
            value: "assignments",
          },
        ]
      : []),
  ];
  const breadcrumbs: BreadcrumbProps[] | undefined = useBreadcrumbs({
    page,
    roleplay,
    scenario,
  });

  const [selectedTab, setSelectedTab] = useState<Tab>(SCENARIO_TABS_CONFIG[0]);
  const navigate = useNavigate();
  useEffect(() => {
    const path = window.location.pathname;
    const isAssignment = path.includes("/assignment");
    if (isAssignment && isAdmin && SCENARIO_TABS_CONFIG[1]) {
      setSelectedTab(SCENARIO_TABS_CONFIG[1]);
    } else {
      setSelectedTab(SCENARIO_TABS_CONFIG[0]);
    }
  }, [window.location.pathname, scenario, isAdmin]);

  function breadcrumbRenderer(props: BreadcrumbProps) {
    if (loading) {
      return <Box className={Classes.SKELETON} height={20} width={83} />;
    }
    return (
      <StyledBreadcrumb title={String(props.text)}>
        {props.href ? <Link to={props.href}>{props.text}</Link> : props.text}
      </StyledBreadcrumb>
    );
  }

  const hasIcon = Boolean(roleplay?.emojiUnicode) || loading;

  return (
    <>
      <Box className="bbs" py={py} bg={Colors.WHITE} px={px} pb={0}>
        <Container style={noMargins ? { margin: "revert" } : {}}>
          <Box mb={noMargins ? 0 : 2}>
            <Breadcrumbs
              items={breadcrumbs}
              breadcrumbRenderer={breadcrumbRenderer}
            />
          </Box>
          <div className="flex flex-col gap-5 md:flex-row justify-start md:justify-between md:items-center">
            <Flex alignItems="center">
              {hasIcon ? (
                <Media greaterThan="xs">
                  {loading || !roleplay ? (
                    <Box className={Classes.SKELETON} height={33} width={26} />
                  ) : (
                    <Emoji
                      unified={roleplay.emojiUnicode.toLowerCase()}
                      emojiStyle={EmojiStyle.APPLE}
                      size={26}
                    />
                  )}
                </Media>
              ) : null}
              <Box ml={hasIcon ? [0, 3] : 0} width="100%">
                <Box
                  fontWeight={600}
                  fontSize={26}
                  lineHeight={1}
                  className={loading ? Classes.SKELETON : ""}
                  width={loading ? 400 : "auto"}
                  height={loading ? 33 : "auto"}
                >
                  <HeaderTitle
                    page={page}
                    roleplay={roleplay}
                    scenario={scenario}
                  />
                </Box>
              </Box>
            </Flex>
            <div className="md:flex-end">
              {page === "scenarioDetail" && scenario && onEditScenario && (
                <ScenarioDetailMenu
                  scenario={scenario}
                  onEditScenario={onEditScenario}
                  refetchAssignments={refetchAssignments}
                />
              )}
            </div>
          </div>
          {page === "scenarioDetail" && scenario ? (
            <Tabs
              tabs={SCENARIO_TABS_CONFIG.map((tab) => ({
                key: tab.key,
                label: tab.label,
                value: tab.key,
              }))}
              tab={selectedTab}
              onTabChange={(tab) => {
                setSelectedTab(tab);
                navigate(tab.key);
              }}
              style={{
                borderTop: "none",
                boxShadow: "none",
              }}
              backgroundColor="transparent"
            />
          ) : (
            <div className="mb-8" />
          )}
        </Container>
      </Box>
      {!loading && <RoleplayLimitBanner page={page} />}
    </>
  );
}

const StyledBreadcrumb = styled(Box)`
  max-width: 200px;
  color: ${SUBTEXT_COLOR};
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  display: inline-block;
  font-size: 14px;

  a {
    font-weight: 600;
    color: ${SUBTEXT_COLOR};
  }
`;
